import { Quote } from "lucide-react";
import { TESTIMONIALS, TOOLS, STEPS } from "@/lib/content";
import { BookButton } from "./Buttons";
import { Eyebrow, Reveal } from "./motion";

export const CTABand = ({ title = "You don’t need all the answers to start a conversation." }) => (
  <section data-testid="cta-band" className="bg-[#082F24] grain relative border-t-4 border-[#D4AF37]">
    <div data-testid="cta-band-content" className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28">
      <Reveal className="relative max-w-3xl">
        <Eyebrow light>Free 30-minute consultation</Eyebrow>
        <h2 data-testid="cta-band-title" className="section-heading mt-6 !text-[#FAF8F5]">{title}</h2>
        <p data-testid="cta-band-description" className="mt-6 text-[#E8F3EE]/85 max-w-xl leading-relaxed">Bring the question that keeps coming back: Can I afford to hire? Are these numbers right? What do I need for funding? Let’s work out the right next step together.</p>
        <div className="mt-10"><BookButton testId="cta-band-book-btn">Book your free conversation</BookButton></div>
      </Reveal>
    </div>
  </section>
);

export const Testimonials = () => (
  <section data-testid="testimonials-section" className="bg-[#082F24] grain relative text-[#FAF8F5] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Eyebrow light>Testimonials</Eyebrow>
        <h2 data-testid="testimonials-title" className="section-heading mt-6 !text-[#FAF8F5] max-w-3xl">The difference a<br /><em className="text-[#D4AF37]">real partnership makes.</em></h2>
      </Reveal>
      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.12} testId={`testimonial-card-${t.id}`} className="group rounded-3xl border border-[#FAF8F5]/12 bg-[#FAF8F5]/[0.04] p-8 sm:p-10 flex flex-col transition-colors duration-500 hover:bg-[#FAF8F5]/[0.08]">
            <div className="flex items-center justify-between">
              <Quote className="h-10 w-10 text-[#D4AF37]" />
              <span className="text-[#D4AF37] tracking-[0.3em]" aria-label="5 out of 5 stars">★★★★★</span>
            </div>
            <p className="mt-8 font-display text-2xl sm:text-3xl leading-snug italic">{t.quote}</p>
            <div className="mt-auto pt-10 flex items-center gap-4">
              <div className="h-16 w-24 shrink-0 overflow-hidden rounded-xl bg-white p-1.5"><img src={t.logo} alt={`${t.role.split(",")[1]} logo`} className="h-full w-full object-contain" /></div>
              <div>
                <p className="font-semibold">{t.name}</p>
                <p className="text-sm text-[#E8F3EE]/75">{t.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ToolChip = ({ t }) => (
  <div data-testid={`tool-logo-${t.name.toLowerCase().replaceAll(" ", "-")}`} className="flex h-24 min-w-0 items-center justify-center rounded-lg bg-white border border-[#0F5A47]/15 px-6 shadow-[0_4px_16px_-12px_rgba(8,47,36,0.25)] transition-[border-color,box-shadow] duration-300 hover:border-[#D4AF37] hover:shadow-[0_8px_20px_-12px_rgba(8,47,36,0.35)]">
    <img data-testid={`tool-image-${t.name.toLowerCase().replaceAll(" ", "-")}`} src={t.src} alt={t.name} className="max-h-12 max-w-full object-contain" />
  </div>
);

export const ToolsMarquee = () => (
  <section data-testid="tools-marquee" className="bg-[#F3EFEA] py-24 overflow-hidden">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-6 lg:grid-cols-2 items-end">
      <Reveal>
        <Eyebrow>Tools We Work With</Eyebrow>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight leading-[1] text-[#082F24]">Trusted platforms, <em>AI-assisted</em> strategy.</h2>
      </Reveal>
      <Reveal delay={0.1}><p className="text-[#52635B] lg:max-w-md lg:ml-auto">We work with trusted platforms to streamline accounting, reporting, and AI-assisted strategy, meeting you inside the systems you already use or setting up the right ones from scratch.</p></Reveal>
    </div>
    <div className="mx-auto max-w-7xl px-6 lg:px-10 mt-12 relative">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TOOLS.map((t) => <ToolChip key={t.name} t={t} />)}
      </div>
    </div>
  </section>
);

export const Process = () => (
  <section data-testid="process-section" className="bg-[#FAF8F5] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Eyebrow>How We Work Together</Eyebrow>
        <h2 data-testid="process-title" className="section-heading mt-6 max-w-3xl">A clear start.<br /><em>A partnership that makes sense.</em></h2>
      </Reveal>
      <ol className="mt-16 grid gap-px bg-[#0F5A47]/15 border border-[#0F5A47]/15 rounded-3xl overflow-hidden sm:grid-cols-2 lg:grid-cols-5">
        {STEPS.map((s, i) => (
          <li key={s.title} data-testid={`process-step-${i + 1}`} className="group bg-[#FAF8F5] p-8 min-h-[16rem] flex flex-col transition-colors duration-500 hover:bg-[#082F24]">
            <span className="font-mono-ega text-sm text-[#0F5A47] group-hover:text-[#D4AF37] transition-colors">0{i + 1}</span>
            <h3 className="mt-auto font-display text-3xl text-[#082F24] group-hover:text-[#FAF8F5] transition-colors">{s.title}</h3>
            <p className="mt-3 text-sm text-[#52635B] group-hover:text-[#E8F3EE]/85 transition-colors">{s.text}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);
